import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useIsAuthenticated } from '@azure/msal-react';

import { OCSOrderTrackerPage, orderSummaryPage } from 'features/order-tracker/router/path-constants';
import { orderTrackerService } from 'features/order-tracker/store/sagas/services/orderTrackerService';
import { getAEMEntryFormProps } from 'features/order-tracker/store/selectors';
import { getHostname } from 'features/order-tracker/utils';
import { AQA_ELEMENTS_MARKER_LIST } from 'features/order-tracker/utils/aqaElementsIDList';

import { FormInput } from './components';
import {
  BUTTON_TXT,
  DEFAULT_VALIDATION_STATUS,
  KEY_ENTER,
  ORDER_NUMBER_LABEL,
  ORDER_NUMBER_TOOLTIP_PROPS,
  ORDER_NUMBER_TOOLTIP_TITLE,
  POST_CODE_LABEL,
  POST_CODE_TOOLTIP_PROPS,
  POST_CODE_TOOLTIP_TITLE,
} from './constants';
import { isSingleOrGroupOrderNumber, validateForm } from './helpers';

import styles from './styles.module.scss';

export const Form: React.FC<sharedComponents.Form> = ({ hasPredefinedValues }) => {
  const isAuthenticated = useIsAuthenticated();
  const hostName = getHostname();
  const navigate = useNavigate();
  const {
    anonymousButtonLabel = BUTTON_TXT,
    authorizedButtonLabel = BUTTON_TXT,
    orderNumberTooltip = ORDER_NUMBER_TOOLTIP_TITLE,
    postcodeTooltip = POST_CODE_TOOLTIP_TITLE,
  } = useSelector(getAEMEntryFormProps) || {};
  const { orderReference = '' } = hasPredefinedValues ? orderTrackerService.getUrlParams() : {};
  const [orderNumber, setOrderNumber] = useState(orderReference);
  const [postCode, setPostCode] = useState('');
  const [isShowPostcodeField, setIsShowPostcodeField] = useState(isSingleOrGroupOrderNumber(orderNumber));
  const [validationStatus, setValidationStatus] = useState(
    DEFAULT_VALIDATION_STATUS as sharedComponents.ValidationStatusType,
  );
  const submitButtonLabel = isAuthenticated ? authorizedButtonLabel : anonymousButtonLabel;

  const handleSubmit = () => {
    const validationResult = validateForm(orderNumber, postCode);
    setValidationStatus(validationResult);
    const formattedOrderNumber = orderNumber.toLocaleLowerCase();

    if (validationResult.orderNumber.isValid && !isShowPostcodeField) {
      window.location.assign(`${hostName}${OCSOrderTrackerPage}${formattedOrderNumber}`);
    } else if (validationResult.postCode.isValid && validationResult.orderNumber.isValid) {
      const formattedPostCode = postCode.toLocaleLowerCase();
      navigate(`${orderSummaryPage}?orn=${formattedOrderNumber}&postcode=${formattedPostCode}`);
    }
  };

  const onFormSubmit = (event) => {
    event.preventDefault();
    handleSubmit();
  };

  const onEnterKeySubmit = (key) => {
    if (key === KEY_ENTER) {
      handleSubmit();
    }
  };

  const handlerOrderNumberFieldChange = (value) => {
    if (!isShowPostcodeField) {
      setPostCode('');
    }

    setValidationStatus(DEFAULT_VALIDATION_STATUS);
    setOrderNumber(value);
    setIsShowPostcodeField(isSingleOrGroupOrderNumber(value));
  };

  return (
    <form
      onSubmit={onFormSubmit}
      className={styles.entryPageForm}
    >
      <div className={styles.fields}>
        <FormInput
          id={AQA_ELEMENTS_MARKER_LIST.entryPage.ornInput}
          value={orderNumber}
          onInputChange={handlerOrderNumberFieldChange}
          onKeyUpSubmit={onEnterKeySubmit}
          label={ORDER_NUMBER_LABEL}
          errorText={validationStatus.orderNumber.errorMassage}
          tooltip={{ ...ORDER_NUMBER_TOOLTIP_PROPS, title: orderNumberTooltip }}
          testId="orderNumber"
        />
        {isShowPostcodeField && (
          <FormInput
            testId="postCode"
            id={AQA_ELEMENTS_MARKER_LIST.entryPage.pcodeInput}
            value={postCode}
            onInputChange={setPostCode}
            onKeyUpSubmit={onEnterKeySubmit}
            label={POST_CODE_LABEL}
            errorText={isShowPostcodeField ? validationStatus.postCode.errorMassage : ''}
            tooltip={{ ...POST_CODE_TOOLTIP_PROPS, title: postcodeTooltip }}
          />
        )}
      </div>
      <button
        id={AQA_ELEMENTS_MARKER_LIST.entryPage.viewOrderStatusButton}
        type="submit"
        data-analytics-link={BUTTON_TXT}
        className={styles.submitButton}
      >
        {submitButtonLabel}
      </button>
    </form>
  );
};
